import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WooCommerceCard from '../ui/WooCommerceCard';
import ConnectWooCommerceModal from '../ui/ConnectWooCommerceModal';
import DisconnectModal from '../ui/DisconnectModal';
import ApiService from '../../services/ApiService';
import toast from 'react-hot-toast';

const WooCommerceIntegrations = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(null);
  const [pollingForConnect, setPollingForConnect] = useState(false);
  const pollTimeoutRef = useRef(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getWooCommerceStatus();
      const list = data?.success ? data.accounts ?? [] : [];
      setAccounts(list);
      return list;
    } catch (err) {
      console.error('Error fetching WooCommerce status:', err);
      setError('Failed to fetch WooCommerce integration status.');
      toast.error('Could not load WooCommerce integration status.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // App-auth return: poll until the callback has created or updated an integration.
  // Reconnecting the same store reuses integration_id, so we also treat an
  // updated_at / connected_at change (or any account appearing when none were
  // known) as success — not only a brand-new id.
  useEffect(() => {
    const state = searchParams.get('state');
    if (!state) {
      fetchStatus();
      return;
    }

    setPollingForConnect(true);
    let attempts = 0;
    const maxAttempts = 20; // ~40s — background finalize needs a bit of time
    /** @type {Map<string, string>} integration_id → connected_at|updated snapshot */
    const knownSnapshot = new Map();
    let cancelled = false;

    const snapshotKey = (a) =>
      `${a.connected_at || ''}|${a.sync?.last_synced_at || ''}|${a.overall_status || ''}|${a.webhook?.is_receiving_events ? '1' : '0'}`;

    const finishSuccess = (account) => {
      sessionStorage.removeItem('wc_auth_state');
      setPollingForConnect(false);
      toast.success('WooCommerce store connected!');
      navigate(`/integrations/woocommerce/${account.integration_id}`, { replace: true });
    };

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      const current = await fetchStatus();

      // 1) Brand-new integration id
      const brandNew = current.find((a) => a.integration_id && !knownSnapshot.has(a.integration_id));
      if (brandNew) {
        finishSuccess(brandNew);
        return;
      }

      // 2) Existing integration updated (reconnect same store — same id)
      const updated = current.find((a) => {
        if (!a.integration_id || !knownSnapshot.has(a.integration_id)) return false;
        return snapshotKey(a) !== knownSnapshot.get(a.integration_id);
      });
      if (updated) {
        finishSuccess(updated);
        return;
      }

      // 3) Had no accounts before, now have at least one (callback won the race
      //    before our first snapshot)
      if (knownSnapshot.size === 0 && current.length > 0) {
        finishSuccess(current[0]);
        return;
      }

      if (attempts >= maxAttempts) {
        setPollingForConnect(false);
        // Prefer showing the store if it is already there rather than a hard error
        if (current.length > 0) {
          sessionStorage.removeItem('wc_auth_state');
          toast.success('WooCommerce store connected!');
          navigate(`/integrations/woocommerce/${current[0].integration_id}`, { replace: true });
          return;
        }
        navigate('/integrations/woocommerce', { replace: true });
        toast.error(
          'Connection is taking longer than expected. If you approved the request in WordPress, refresh this page in a moment.'
        );
        return;
      }
      pollTimeoutRef.current = setTimeout(poll, 2000);
    };

    ApiService.getWooCommerceStatus()
      .then((data) => {
        (data?.accounts ?? []).forEach((a) => {
          if (a.integration_id) knownSnapshot.set(a.integration_id, snapshotKey(a));
        });
        poll();
      })
      .catch(() => {
        poll();
      });

    return () => {
      cancelled = true;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, [searchParams]);

  const handleDisconnect = async ({ purgeContent = false } = {}) => {
    if (!selectedIntegrationId) return;
    try {
      setIsDisconnecting(true);
      const response = await ApiService.disconnectWooCommerce(selectedIntegrationId, {
        purgeContent,
      });
      if (response?.success) {
        toast.success(
          purgeContent
            ? 'Store disconnected and product content purged.'
            : 'WooCommerce store disconnected.'
        );
        setAccounts((prev) => prev.filter((a) => a.integration_id !== selectedIntegrationId));
        setIsDisconnectModalOpen(false);
        setSelectedIntegrationId(null);
      } else {
        throw new Error(response?.message || 'Failed to disconnect.');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to disconnect WooCommerce.');
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <div className="integrations-root p-6 sm:p-8">
      <div className="flex items-center gap-4 mb-10 border-b border-gray-100 pb-6">
        <button
          onClick={() => navigate('/integrations')}
          className="p-2 hover:bg-gray-100 rounded-xl"
        >
          <ArrowLeft className="h-6 w-6 text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2 tracking-tight">
            WooCommerce Integrations
          </h1>
          <p className="text-gray-500 font-medium">
            Sync your product catalog and answer store questions with AI
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-8">
        {pollingForConnect ? (
          <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm flex flex-col items-center justify-center min-h-[300px]">
            <Loader2 className="h-10 w-10 text-black animate-spin mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Connecting your store</h2>
            <p className="text-gray-500 font-medium text-center max-w-sm">
              Waiting for WordPress to confirm the connection and finish registering webhooks…
            </p>
          </div>
        ) : loading ? (
          <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm flex flex-col items-center justify-center min-h-[300px]">
            <Loader2 className="h-10 w-10 text-black animate-spin mb-4" />
            <p className="text-gray-500 font-medium animate-pulse">Loading WooCommerce status…</p>
          </div>
        ) : error ? (
          <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm flex flex-col items-center text-center min-h-[300px]">
            <AlertCircle className="h-8 w-8 text-red-600 mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">{error}</h3>
            <button
              onClick={fetchStatus}
              className="flex items-center gap-2 px-5 py-2.5 bg-black text-white font-bold rounded-xl"
            >
              <RefreshCw className="h-4 w-4" /> Try Again
            </button>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {accounts.length === 0 && (
                <WooCommerceCard
                  account={null}
                  onConnect={() => setConnectModalOpen(true)}
                  onManage={() => {}}
                  onDisconnect={() => {}}
                />
              )}
              {accounts.map((acc) => (
                <WooCommerceCard
                  key={acc.integration_id}
                  account={acc}
                  onManage={() => navigate(`/integrations/woocommerce/${acc.integration_id}`)}
                  onDisconnect={(id) => {
                    setSelectedIntegrationId(id);
                    setIsDisconnectModalOpen(true);
                  }}
                />
              ))}
              {accounts.length > 0 && (
                <button
                  onClick={() => setConnectModalOpen(true)}
                  className="group flex flex-col items-center justify-center p-6 bg-white border-2 border-dashed border-gray-200 rounded-[1.5rem] hover:border-black min-h-[220px]"
                >
                  <RefreshCw className="w-6 h-6 text-gray-400 group-hover:text-black mb-3" />
                  <span className="text-gray-900 font-bold text-sm">Connect another store</span>
                </button>
              )}
            </div>
          </motion.div>
        )}
      </div>

      <ConnectWooCommerceModal
        isOpen={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        onManualConnected={(newAccount) => {
          setConnectModalOpen(false);
          if (newAccount?.integration_id) {
            navigate(`/integrations/woocommerce/${newAccount.integration_id}`);
          } else {
            fetchStatus();
          }
        }}
      />

      <DisconnectModal
        isOpen={isDisconnectModalOpen}
        onClose={() => setIsDisconnectModalOpen(false)}
        onConfirm={handleDisconnect}
        integrationName="WooCommerce"
        isLoading={isDisconnecting}
        showPurgeOption
        purgeLabel="Also delete synced product content from the knowledge base (vectors + content docs)"
      />
    </div>
  );
};

export default WooCommerceIntegrations;
